/**
 * Thin, typed wrapper over the GTM dataLayer.
 *
 * The container id is not hardcoded. SettingsService injects whatever
 * `googleTagManagerId` the deployment has stored, and that is the only
 * loader. Production (flogpt.com, checked 2026-10-12) serves GTM-TC4W6T77.
 * An older note in this file named GTM-WJSV72VG; that container is not what
 * the live site loads, and baking either id in here would fire the wrong
 * container from QA.
 *
 * GA4, Google Ads, and the Meta Pixel are tags inside that container. This
 * module only pushes events. It does not call `fbq('track', 'PageView')`:
 * the pixel's PageView already runs from GTM, and a second call from the
 * app is how the live homepage was counting the view twice.
 *
 * dataLayer is created lazily here so events fired before gtm.js finishes
 * loading are queued in the array and replayed by GTM on init.
 */

type DataLayerObject = Record<string, unknown>;

declare global {
    interface Window {
        dataLayer?: DataLayerObject[];
        /** Meta pixel snippet, installed by a GTM tag. Absent until that tag runs. */
        fbq?: (...args: unknown[]) => void;
    }
}

/**
 * Ad-click parameters forwarded onto a conversion so GA4 and Google Ads can
 * attribute the event without reading the URL again after the SPA navigates.
 */
export interface ConversionUtm {
    utmSource?: string | null;
    utmMedium?: string | null;
    utmCampaign?: string | null;
    utmTerm?: string | null;
    utmContent?: string | null;
}

/** Conversion/interaction events the app emits. Mirror these as triggers in GTM. */
export type AnalyticsEvent =
    | { event: "spa_pageview"; page_path: string; page_title: string; page_location: string }
    | {
          event: "sign_up";
          method: "password";
          industry?: string;
          platform?: string;
          referred?: boolean;
          event_id?: string;
          form?: string;
          /**
           * True when this page already called `fbq` for the matching Meta
           * standard event. The GTM Meta tag on `sign_up` / `generate_lead`
           * should exception this, or copy `event_id` into the tag's Event ID.
           */
          skip_gtm_meta?: boolean;
      }
    | {
          event: "generate_lead";
          form: string;
          value?: number;
          currency?: string;
          event_id?: string;
          industry?: string | null;
          skip_gtm_meta?: boolean;
      };

export function pushToDataLayer(payload: AnalyticsEvent | DataLayerObject): void {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
}

/**
 * Virtual pageview for the SPA. GA4's Google tag counts the initial document
 * load itself, so callers should skip the first route to avoid double-counting
 * (see MetaTagsManager). In GTM, build a GA4 `page_view` event tag on the
 * `spa_pageview` trigger.
 */
export function trackPageView(path: string): void {
    pushToDataLayer({
        event: "spa_pageview",
        page_path: path,
        page_title: document.title,
        page_location: window.location.href,
    });
}

function utmPayload(params: ConversionUtm): DataLayerObject {
    const fields: DataLayerObject = {};
    const pairs: Array<[keyof ConversionUtm, string]> = [
        ["utmSource", "utm_source"],
        ["utmMedium", "utm_medium"],
        ["utmCampaign", "utm_campaign"],
        ["utmTerm", "utm_term"],
        ["utmContent", "utm_content"],
    ];
    for (const [field, key] of pairs) {
        const value = params[field];
        if (typeof value === "string" && value.trim()) fields[key] = value.trim();
    }
    return fields;
}

interface SignupConversion extends ConversionUtm {
    industry?: string;
    platform?: string;
    referred?: boolean;
    /** Shared with the Meta browser event and the Conversions API. */
    eventId?: string;
    form?: string;
    /**
     * The page already sent this conversion to Meta with `fbq`. GTM's Meta
     * tag on `generate_lead` (container GTM-TC4W6T77) has no event ID, so a
     * second Lead would be counted. `false` is pushed as well: the dataLayer
     * keeps the previous value, and a sticky `true` would suppress the Meta
     * tag for a later schedule-booking lead.
     */
    skipGtmMeta?: boolean;
}

export function trackSignup(params: SignupConversion = {}): void {
    pushToDataLayer({
        event: "sign_up",
        method: "password",
        industry: params.industry || undefined,
        platform: params.platform || undefined,
        referred: params.referred,
        ...(params.eventId ? { event_id: params.eventId } : {}),
        ...(params.form ? { form: params.form } : {}),
        skip_gtm_meta: params.skipGtmMeta === true,
        ...utmPayload(params),
    });
}

export function trackLead(
    params: SignupConversion & { value?: number; currency?: string; eventId?: string } = {},
): void {
    pushToDataLayer({
        event: "generate_lead",
        // Every generate_lead carries an id. Schedule booking has no server
        // twin; the signup flow passes the same id the Meta Lead uses.
        event_id: params.eventId || crypto.randomUUID(),
        form: params.form || "schedule_booking",
        // null, not omitted: a previous signup push would otherwise stay on
        // the dataLayer and land on the next schedule-booking lead.
        industry: params.industry || null,
        value: params.value,
        currency: params.currency,
        skip_gtm_meta: params.skipGtmMeta === true,
        ...utmPayload(params),
    });
}

/** Meta standard events this app emits itself. PageView is not one of them. */
export type StandardMetaEvent = "Lead" | "CompleteRegistration";

/**
 * Push a Meta standard event to GTM and, if the pixel is already on the page,
 * to `fbq` with the same event id.
 *
 * Signup also pushes `generate_lead` / `sign_up` (see `trackLead` /
 * `trackSignup`) with this same `eventId`, because GA4 and Google Ads listen
 * for those names. The direct `fbq` call is what reaches Meta today. GTM's
 * Meta Lead tag listens for `generate_lead` and does not set an event ID, so
 * those pushes set `skip_gtm_meta` for that tag to exclude.
 */
export function trackStandardEvent(name: StandardMetaEvent, eventId: string, params: DataLayerObject = {}): void {
    pushToDataLayer({
        event: name,
        event_id: eventId,
        ...params,
    });
    if (typeof window.fbq === "function") {
        window.fbq("track", name, params, { eventID: eventId });
    }
}
